import { useState } from "react";
import { Heart, Facebook, Instagram, Linkedin, Twitter, Mail, ArrowRight, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useLanguage } from "../contexts/LanguageContext";
import api, { formatApiError } from "../lib/api";

const NewsletterForm = () => {
  const { t, lang } = useLanguage();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", { email: email.trim(), locale: lang });
      if (data.status === "already_subscribed") {
        toast.info(t.footer.newsletter.alreadySubscribed);
      } else {
        toast.success(t.footer.newsletter.success);
      }
      setDone(true);
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t.footer.newsletter.error);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="flex items-center gap-2 text-[#E8C77E] text-sm py-2.5" data-testid="newsletter-done">
        <Check size={16} className="shrink-0" />
        <span>{t.footer.newsletter.thanks}</span>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="flex gap-2 mt-2" data-testid="newsletter-form">
      <div className="relative flex-1">
        <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t.footer.newsletter.placeholder}
          className="w-full bg-white/5 border border-white/15 rounded-md pl-9 pr-3 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#C5A059]"
          data-testid="newsletter-email-input"
        />
      </div>
      <button
        type="submit"
        disabled={loading}
        className="px-4 py-2.5 rounded-md bg-[#C5A059] hover:bg-[#B08D4A] text-white text-sm font-medium disabled:opacity-50 transition-colors flex items-center gap-1"
        data-testid="newsletter-submit"
      >
        {loading ? "…" : <ArrowRight size={14} />}
      </button>
    </form>
  );
};

const Footer = () => {
  const { t } = useLanguage();
  return (
    <footer className="bg-[#002B49] text-white pt-20 pb-8" data-testid="footer">
      <div className="container-narrow">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                <Heart size={18} fill="#C5A059" className="text-[#C5A059]" />
              </div>
              <div>
                <div className="font-serif text-xl font-semibold">Cœur Solidaire</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/60">
                  Fondation
                </div>
              </div>
            </div>
            <p className="font-serif italic text-[#E8C77E] text-lg mt-6 leading-relaxed">
              « {t.footer.tagline} »
            </p>
            <p className="text-white/60 text-sm mt-4 leading-relaxed max-w-md">
              {t.footer.registered}
            </p>
            <div className="flex gap-3 mt-6">
              {[Facebook, Instagram, Linkedin, Twitter].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  onClick={(e) => e.preventDefault()}
                  className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center hover:bg-[#C5A059] hover:border-[#C5A059] transition-all"
                  aria-label={`social-${i}`}
                  data-testid={`social-link-${i}`}
                >
                  <Icon size={15} />
                </a>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-white/10">
              <h4 className="text-xs uppercase tracking-[0.18em] text-[#C5A059] mb-2 font-semibold">
                {t.footer.newsletter.title}
              </h4>
              <p className="text-white/60 text-sm leading-relaxed">
                {t.footer.newsletter.subtitle}
              </p>
              <NewsletterForm />
            </div>
          </div>

          <div className="md:col-span-3">
            <h4 className="text-xs uppercase tracking-[0.18em] text-[#C5A059] mb-4 font-semibold">
              Navigation
            </h4>
            <ul className="space-y-2.5 text-sm">
              <FooterLink id="mission">{t.nav.mission}</FooterLink>
              <FooterLink id="impact">{t.nav.impact}</FooterLink>
              <FooterLink id="donate">{t.nav.donate}</FooterLink>
              <FooterLink id="transparency">{t.nav.transparency}</FooterLink>
              <FooterLink id="contact">{t.nav.contact}</FooterLink>
              <li>
                <Link
                  to="/temoignages"
                  className="text-white/70 hover:text-white transition-colors"
                  data-testid="footer-testimonials-link"
                >
                  {t.testimonialsPage?.eyebrow || "Témoignages"}
                </Link>
              </li>
              <li>
                <Link
                  to="/conseil-administration"
                  className="text-white/70 hover:text-white transition-colors"
                  data-testid="footer-board-link"
                >
                  {t.boardPage?.navTitle || "Conseil d'administration"}
                </Link>
              </li>
              <li>
                <Link
                  to="/actualites"
                  className="text-white/70 hover:text-white transition-colors"
                  data-testid="footer-blog-link"
                >
                  {t.blogPage?.eyebrow || "Actualités"}
                </Link>
              </li>
              <li>
                <Link
                  to="/carte-impact"
                  className="text-white/70 hover:text-white transition-colors"
                  data-testid="footer-impact-map-link"
                >
                  {t.impactMapPage?.eyebrow || "Notre présence dans le monde"}
                </Link>
              </li>
              <li>
                <Link
                  to="/campagnes"
                  className="text-white/70 hover:text-white transition-colors"
                  data-testid="footer-campaigns-link"
                >
                  {t.campaignsPage?.eyebrow || "Campagnes en cours"}
                </Link>
              </li>
              <li>
                <Link
                  to="/legs"
                  className="text-white/70 hover:text-white transition-colors"
                  data-testid="footer-legacy-link"
                >
                  {t.legacyPage?.eyebrow || "Faire un legs"}
                </Link>
              </li>
              <li>
                <Link
                  to="/benevole"
                  className="text-white/70 hover:text-white transition-colors"
                  data-testid="footer-volunteer-link"
                >
                  {t.volunteerPage?.eyebrow || "Bénévole / Partenaire"}
                </Link>
              </li>
              <li>
                <Link
                  to="/mon-espace"
                  className="text-white/70 hover:text-[#C5A059] transition-colors"
                  data-testid="footer-donor-portal-link"
                >
                  {t.donorPortal?.eyebrow || "Espace donateur"}
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="text-xs uppercase tracking-[0.18em] text-[#C5A059] mb-4 font-semibold">
              Légal
            </h4>
            <ul className="space-y-2.5 text-sm">
              <li><a href="#" className="text-white/70 hover:text-white transition-colors" data-testid="footer-legal" onClick={(e) => e.preventDefault()}>{t.footer.links.legal}</a></li>
              <li><a href="#" className="text-white/70 hover:text-white transition-colors" data-testid="footer-privacy" onClick={(e) => e.preventDefault()}>{t.footer.links.privacy}</a></li>
              <li><a href="#" className="text-white/70 hover:text-white transition-colors" data-testid="footer-cookies" onClick={(e) => e.preventDefault()}>{t.footer.links.cookies}</a></li>
              <li><Link to="/admin/login" className="text-white/70 hover:text-[#C5A059] transition-colors" data-testid="footer-admin-link">{t.footer.links.admin}</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 text-center">
          <p className="text-white/55 text-xs italic" data-testid="footer-collective-mention">
            {t.footer.collective}
          </p>
        </div>

        <div className="mt-8 pt-6 border-t border-white/10 flex flex-col md:flex-row gap-3 justify-between items-center text-xs text-white/50">
          <div>{t.footer.copyright}</div>
          <div className="flex items-center gap-2">
            Made with <Heart size={12} fill="#C5A059" className="text-[#C5A059]" /> in France
          </div>
        </div>
      </div>
    </footer>
  );
};

const FooterLink = ({ id, children }) => {
  const handle = () => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <li>
      <button
        onClick={handle}
        className="text-white/70 hover:text-white transition-colors text-left"
        data-testid={`footer-link-${id}`}
      >
        {children}
      </button>
    </li>
  );
};

export default Footer;
